import React, { useEffect, useState } from 'react';
import Logo from '../../components/common/Logo';
import AdminSidebar from '../../components/dashboard-admin/AdminSidebar';
import UserService from '../../services/user.service';
import AdminDoctorService from '../../services/admin.doctor.service';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Power, User, Calendar, Tag, Clock, Briefcase, Trash2 } from 'lucide-react';
import DoctorAdminSettings from '../../components/dashboard-admin/DoctorDetailSettings';
import DoctorCalendar from '../../components/admin/AdminDoctorCalendar';
import AdminTimePreferenceForm from '../../components/dashboard-admin/AdminDoctorTimePreferenceForm';
import AmministrationTagManagment from '../../components/admin/AmministrationTagManagement';
import AdminDoctorServices from '../../components/dashboard-admin/AdminDoctorServices';
import AdminDoctorTagsBox from '../../components/dashboard-admin/AdminDoctorTagsBox';
import DeleteUserButton from '../../components/DeleteUserButton';

const AdminDoctorDetailPage = () => {
    const [doctor, setDoctor] = useState(null);
    const [professionalTags, setProfessionalTags] = useState([]);
    const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'prof-tags', 'time-prefs', 'tags', 'services', 'calendar', 'delete'
    const { doctorId } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        loadDoctorDetails();
        loadProfessionalTags();
    }, [doctorId]);

    // Carica i tag professionali quando il tab viene selezionato
    useEffect(() => {
        if (activeTab === 'prof-tags') {
            loadProfessionalTags();
        }
    }, [activeTab]);

    const loadDoctorDetails = async () => {
        try {
            const data = await UserService.getUserDetails(doctorId);
            setDoctor(data);
        } catch (error) {
            console.error('Errore durante il caricamento dei dettagli del professinista:', error);
        }
    };

    const loadProfessionalTags = async () => {
        try {
            const data = await AdminDoctorService.getProfile(doctorId);
            setProfessionalTags(data.tags || []);
        } catch (error) {
            console.error('[DEBUG] Error loading professional tags:', error);
        }
    };

    const handleToggleLogin = async () => {
        try {
            await UserService.toggleUserLogin(doctorId);
            loadDoctorDetails();
        } catch (error) {
            console.error('[DEBUG] Error toggling login status:', error);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    if (!doctor) {
        return (
            <div className="flex min-h-screen">
                <div className="w-64 bg-white shadow-md">
                    <div className="p-4">
                        <Logo />
                    </div>
                    <AdminSidebar />
                </div>
                <div className="flex-1 bg-gray-50 p-8">
                    <p>Caricamento dettagli professionista...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen">
            <div className="w-64 bg-white shadow-md">
                <div className="p-4">
                    <Logo />
                </div>
                <AdminSidebar />
            </div>

            <div className="flex-1 bg-gray-50 p-8">
                <header className="mb-8">
                    <button 
                        onClick={() => navigate('/admin/doctors')}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Torna alla lista
                    </button>

                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold mb-2">
                                {doctor.first_name} {doctor.last_name}
                            </h1>
                            <p className="text-gray-600">{doctor.email}</p>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                                <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
                                    doctor.login_enabled ? 'bg-green-500' : 'bg-red-500'
                                }`} />
                                <span className="text-sm text-gray-600">
                                    Login {doctor.login_enabled ? 'Abilitato' : 'Disabilitato'}
                                </span>
                            </div>
                            <button
                                onClick={handleToggleLogin}
                                className={`px-4 py-2 rounded-lg flex items-center ${
                                    doctor.login_enabled 
                                        ? 'bg-red-500 hover:bg-red-600' 
                                        : 'bg-green-500 hover:bg-green-600'
                                } text-white transition-colors`}
                            >
                                <Power className="w-4 h-4 mr-2" />
                                {doctor.login_enabled ? 'Disabilita' : 'Abilita'}
                            </button>
                        </div>
                    </div>
                </header>

                {/* Tabs Navigation */}
                <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                    <button
                        className={`py-3 px-6 font-medium flex items-center border-b-2 ${
                            activeTab === 'profile'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => handleTabChange('profile')}
                    >
                        <User className="w-5 h-5 mr-2" />
                        Profilo
                    </button>
                    <button
                        className={`py-3 px-6 font-medium flex items-center border-b-2 ${
                            activeTab === 'prof-tags'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => handleTabChange('prof-tags')}
                    >
                        <Tag className="w-5 h-5 mr-2" />
                        Tag Professionali
                    </button>
                    <button
                        className={`py-3 px-6 font-medium flex items-center border-b-2 ${
                            activeTab === 'time-prefs'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => handleTabChange('time-prefs')}
                    >
                        <Clock className="w-5 h-5 mr-2" />
                        Preferenza Orari
                    </button>
                    <button
                        className={`py-3 px-6 font-medium flex items-center border-b-2 ${
                            activeTab === 'tags'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => handleTabChange('tags')}
                    >
                        <Tag className="w-5 h-5 mr-2" />
                        Tag Amministrativi
                    </button>
                    <button
                        className={`py-3 px-6 font-medium flex items-center border-b-2 ${
                            activeTab === 'services'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => handleTabChange('services')}
                    >
                        <Briefcase className="w-5 h-5 mr-2" />
                        Servizi
                    </button>
                    <button
                        className={`py-3 px-6 font-medium flex items-center border-b-2 ${
                            activeTab === 'calendar'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => handleTabChange('calendar')}
                    >
                        <Calendar className="w-5 h-5 mr-2" />
                        Calendario
                    </button>
                    <button
                        className={`py-3 px-6 font-medium flex items-center border-b-2 ${
                            activeTab === 'delete'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => handleTabChange('delete')}
                    >
                        <Trash2 className="w-5 h-5 mr-2" />
                        Cancellazione
                    </button>
                </div>

                {/* Tab Content */}
                <div>
                    {activeTab === 'profile' && (
                        <DoctorAdminSettings 
                            doctorId={doctorId}
                            onUpdate={loadDoctorDetails}
                        />
                    )}
                    
                    {activeTab === 'prof-tags' && (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-lg font-semibold mb-4">Gestione Tag Professionali</h2>
                            <AdminDoctorTagsBox 
                                therapistId={Number(doctorId)} 
                                initialTags={professionalTags}
                            />
                        </div>
                    )}
                    
                    {activeTab === 'time-prefs' && (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-lg font-semibold mb-4">Gestione Preferenze Orarie</h2>
                            <AdminTimePreferenceForm userId={doctorId} />
                        </div>
                    )}
                    
                    {activeTab === 'tags' && (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-lg font-semibold mb-4">Gestione Tag Amministrativi</h2>
                            <AmministrationTagManagment therapistId={Number(doctorId)} initialTags={[]} />
                        </div>
                    )}
                    
                    {activeTab === 'services' && (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-lg font-semibold mb-4">Gestione Servizi</h2>
                            <AdminDoctorServices therapistId={Number(doctorId)} />
                        </div>
                    )}
                    
                    {activeTab === 'calendar' && (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-lg font-semibold mb-4">Gestione Calendario e Disponibilità</h2>
                            <DoctorCalendar therapistId={doctorId} />
                        </div>
                    )}
                    
                    {activeTab === 'delete' && (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-lg font-semibold mb-4">Cancellazione</h2>
                            <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-800 mb-2">
                                    <strong>Attenzione:</strong> La cancellazione di un utente è un'operazione irreversibile.
                                </p>
                                <p className="text-red-600">
                                    Tutti i dati associati a questo utente verranno rimossi dal sistema.
                                </p>
                            </div>
                            <DeleteUserButton 
                                userId={doctor.id} 
                                userName={`${doctor.first_name} ${doctor.last_name}`}
                                onSuccess={() => {
                                    navigate('/admin/doctors');
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDoctorDetailPage;